import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft, 
  FaClock, 
  FaCheckCircle, 
  FaTruck, 
  FaBox,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';
import { useOrders } from '../context/OrderContext';
import { useAuth } from '../context/AuthContext';
import './OrderDetail.css';

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getOrderById } = useOrders();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      const currentPath = window.location.pathname;
      navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      const foundOrder = getOrderById(orderId);
      
      if (!foundOrder) {
        navigate('/order-history');
        return;
      }

      setOrder(foundOrder);
      setLoading(false);
    };

    fetchOrder();
  }, [orderId, currentUser, navigate, getOrderById]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'confirmed':
        return <FaCheckCircle className="status-icon confirmed" />;
      case 'processing':
        return <FaBox className="status-icon processing" />;
      case 'shipped':
        return <FaTruck className="status-icon shipped" />;
      case 'delivered':
        return <FaCheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <FaClock className="status-icon cancelled" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#f39c12';
      case 'confirmed':
        return '#3498db';
      case 'processing':
        return '#9b59b6';
      case 'shipped':
        return '#e67e22';
      case 'delivered':
        return '#27ae60';
      case 'cancelled':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="order-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="order-detail-page">
      <div className="container">
        {/* Header */}
        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="header-content">
            <button 
              className="back-button"
              onClick={() => navigate('/order-history')}
            >
              <FaArrowLeft /> Back to Orders
            </button>
            <h1>Order Details</h1>
          </div>
        </motion.div>

        <div className="order-detail-content">
          {/* Order Summary Card */}
          <motion.div
            className="order-summary-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="order-header">
              <div className="order-info">
                <h2>Order #{order.orderNumber}</h2>
                <p className="order-date">
                  Placed on {formatDate(order.createdAt)}
                </p>
              </div>
              <div className="order-status">
                {getStatusIcon(order.status)}
                <span 
                  className="status-text"
                  style={{ color: getStatusColor(order.status) }}
                >
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>
            </div>

            {/* Order Items */}
            <div className="order-items-section">
              <h3>Order Items</h3>
              <div className="order-items">
                {order.items?.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-image">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <p className="item-size">Size: {item.size}</p>
                      <div className="price-quantity">
                        <span className="price">₹{item.price.toLocaleString()}</span>
                        <span className="quantity">× {item.quantity}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-details">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>₹{calculateTotal(order.items || []).toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>Free</span>
                </div>
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>₹{order.totalAmount?.toLocaleString() || calculateTotal(order.items || []).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Shipping Information */}
          <motion.div
            className="shipping-info-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3>Shipping Information</h3>
            <div className="shipping-details">
              <div className="shipping-address">
                <div className="address-header">
                  <FaMapMarkerAlt className="address-icon" />
                  <h4>Shipping Address</h4>
                </div>
                <p>
                  {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}<br />
                  {order.shippingAddress?.address}<br />
                  {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.pinCode}<br />
                  {order.shippingAddress?.landmark && `Landmark: ${order.shippingAddress.landmark}<br />`}
                </p>
              </div>
              
              <div className="contact-info">
                <div className="contact-item">
                  <FaPhone className="contact-icon" />
                  <span>{order.shippingAddress?.phone}</span>
                </div>
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <span>{order.shippingAddress?.email}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Information */}
          <motion.div
            className="payment-info-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h3>Payment Information</h3>
            <div className="payment-details">
              <div className="payment-row">
                <span>Payment Method:</span>
                <span>{order.paymentMethod?.charAt(0).toUpperCase() + order.paymentMethod?.slice(1) || 'N/A'}</span>
              </div>
              <div className="payment-row">
                <span>Payment Status:</span>
                <span className={`payment-status ${order.paymentStatus}`}>
                  {order.paymentStatus?.charAt(0).toUpperCase() + order.paymentStatus?.slice(1) || 'N/A'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
